import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useAccessControlStore } from '../../store/access-control.store';
import {
  ACCESS_REQUIREMENT_KEYS,
  type AccessRequirement,
  type AccessRequirementKey,
  type AccessState,
} from '../../store/access-control.types';
import { getAccessRequirementStepLabel } from '../../store/access-control.utils';

export interface AccessChecklistItem {
  key: AccessRequirementKey;
  stepLabel: string;
  state: AccessRequirement['state'];
  status: AccessRequirement['status'];
  statusLabel: string;
  ctaLabel: string;
  ctaVisible: boolean;
  ctaDisabled: boolean;
  ctaKind: AccessRequirement['ctaKind'];
  isCurrent: boolean;
}

const onboardingSequence = ACCESS_REQUIREMENT_KEYS;

const sortRequirementsByOnboardingSequence = (requirements: AccessRequirement[]) => {
  const requirementMap = new Map(requirements.map((requirement) => [requirement.key, requirement]));

  return onboardingSequence.flatMap((key) => {
    const requirement = requirementMap.get(key);

    return requirement ? [requirement] : [];
  });
};

const resolveCurrentRequirementKey = (requirements: AccessRequirement[]) => {
  return (
    onboardingSequence.find((key) => {
      const requirement = requirements.find((item) => item.key === key);

      return requirement?.state !== 'satisfied';
    }) ?? null
  );
};

const getRequirementStatusLabel = (requirement: AccessRequirement) => {
  switch (requirement.status) {
    case 'GREEN':
      return 'Completed';
    case 'YELLOW':
      return 'Under review';
    case 'RETRY':
      return 'Action needed';
    case 'RED':
      return 'Action needed';
    case 'READY':
      return 'Ready';
    case 'ERROR':
      return 'Unavailable';
    case 'NOT_READY':
      return 'Pending';
    default:
      return 'Not started';
  }
};

const getRequirementCtaLabel = (requirement: AccessRequirement) => {
  switch (requirement.ctaKind) {
    case 'start':
      return 'Start';
    case 'resume':
      if (requirement.status === 'RED') {
        return 'View Details';
      }

      if (requirement.status === 'RETRY') {
        return 'View Status';
      }

      return 'Continue';
    case 'refresh':
      return 'View Status';
    case 'wait':
    case 'none':
    default:
      return '';
  }
};

const getSummaryMessage = (accessState: AccessState) => {
  if (accessState === 'resolving') {
    return "We're checking your access requirements.";
  }

  if (accessState === 'blocked') {
    return 'Complete the remaining onboarding steps to unlock full access.';
  }

  if (accessState === 'pending') {
    return 'Some onboarding steps are still being processed.';
  }

  return '';
};

const getSeverity = (accessState: AccessState) => {
  if (accessState === 'blocked') {
    return 'warn' as const;
  }

  if (accessState === 'pending') {
    return 'secondary' as const;
  }

  return 'secondary' as const;
};

export const useAccessChecklistViewModel = () => {
  const accessControlStore = useAccessControlStore();
  const { accessState, requirements } = storeToRefs(accessControlStore);
  const { runKybCta, runKycCta } = accessControlStore;

  const orderedRequirements = computed(() =>
    sortRequirementsByOnboardingSequence(requirements.value),
  );
  const currentRequirementKey = computed(() =>
    resolveCurrentRequirementKey(orderedRequirements.value),
  );

  const runRequirementCta = async (key: AccessRequirementKey) => {
    if (key === 'kyc') {
      await runKycCta();
      return;
    }

    if (key === 'kyb') {
      await runKybCta();
    }
  };

  const items = computed<AccessChecklistItem[]>(() => {
    return orderedRequirements.value.map((requirement) => {
      const isCurrent = requirement.key === currentRequirementKey.value;
      const ctaVisible =
        isCurrent && requirement.ctaKind !== 'none' && requirement.ctaKind !== 'wait';

      return {
        key: requirement.key,
        stepLabel: getAccessRequirementStepLabel(requirement.key),
        state: requirement.state,
        status: requirement.status,
        statusLabel: getRequirementStatusLabel(requirement),
        ctaLabel: getRequirementCtaLabel(requirement),
        ctaVisible,
        ctaDisabled: !requirement.ctaEnabled,
        ctaKind: requirement.ctaKind,
        isCurrent,
      };
    });
  });

  const currentItem = computed(() => items.value.find((item) => item.isCurrent) ?? null);

  return {
    visible: computed(
      () =>
        accessState.value !== 'resolving' &&
        accessState.value !== 'unauthenticated' &&
        accessState.value !== 'onboarded',
    ),
    severity: computed(() => getSeverity(accessState.value)),
    message: computed(() => getSummaryMessage(accessState.value)),
    items,
    currentItem,
    runRequirementCta,
  };
};
